import React from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Body, MenuPath, Note, SectionDivider, Tip, Warning } from '../ui';

// ── Page ────────────────────────────────────────────────────────────────────

const AnimatingDoc: React.FC = () => {
  const { t } = useTranslation();

  return (
    <article className="font-body text-content-primary">
      {/* -- Critical warning banner -- */}
      <div className="bg-badge-rose-bg border-2 border-badge-rose-text rounded-[10px] px-[24px] py-[20px] mb-[32px]">
        <div className="flex items-center gap-[10px] mb-[8px]">
          <span className="text-[20px] leading-[1]">⚠️</span>
          <span className="font-display text-[16px] font-bold text-badge-rose-text tracking-[-0.2px]">
            {t('docs.animating.warningBannerTitle')}
          </span>
        </div>
        <p className="text-[14px] leading-[1.75] text-badge-rose-text m-0">
          {t('docs.animating.warningBannerBody')}
        </p>
      </div>

      {/* -- Intro -- */}
      <h2
        id="animating-with-touchpose"
        className="font-display text-[24px] font-extrabold text-content-primary  mb-[16px] tracking-[-0.4px]"
      >
        {t('docs.animating.title')}
      </h2>
      <Body>{t('docs.animating.intro')}</Body>

      <SectionDivider />

      {/* -- Enabling -- */}
      <h2
        id="enabling"
        className="font-display text-[24px] font-extrabold text-content-primary  mb-[16px] tracking-[-0.4px]"
      >
        {t('docs.animating.enablingTitle')}
      </h2>
      <Body>
        <Trans
          i18nKey="docs.animating.enablingBody1"
          components={{ strong: <strong />, code: <MenuPath>{''}</MenuPath> }}
        />
      </Body>
      <Body>{t('docs.animating.enablingBody2')}</Body>

      <Note label={t('docs.animating.noteLabel')}>
        <Trans
          i18nKey="docs.animating.enablingNote"
          components={{
            code: <MenuPath>{''}</MenuPath>,
            usec: (
              <Link
                to="/touchpose/docs/getting-started"
                className="text-blue-link no-underline font-medium"
              />
            )
          }}
        />
      </Note>

      <h3 className="font-display text-[15px] font-bold text-content-primary mt-[20px] mb-[6px]">
        {t('docs.animating.enablingIndividualTitle')}
      </h3>
      <Body>
        <Trans
          i18nKey="docs.animating.enablingIndividualBody"
          components={{ code: <MenuPath>{''}</MenuPath> }}
        />
      </Body>

      <SectionDivider />

      {/* -- The overlay -- */}
      <h2
        id="the-overlay"
        className="font-display text-[24px] font-extrabold text-content-primary  mb-[16px] tracking-[-0.4px]"
      >
        {t('docs.animating.whatYouSeeTitle')}
      </h2>
      <Body>{t('docs.animating.whatYouSeeBody')}</Body>

      <h3 className="font-display text-[15px] font-bold text-content-primary mt-[20px] mb-[6px]">
        {t('docs.animating.hoverLabelTitle')}
      </h3>
      <Body>{t('docs.animating.hoverLabelBody')}</Body>

      <Tip label={t('docs.animating.tipLabel')}>
        <Trans
          i18nKey="docs.animating.hoverLabelTip"
          components={{ strong: <strong />, code: <MenuPath>{''}</MenuPath> }}
        />
      </Tip>

      <SectionDivider />

      {/* -- Settings -- */}
      <h2
        id="settings"
        className="font-display text-[24px] font-extrabold text-content-primary  mb-[16px] tracking-[-0.4px]"
      >
        {t('docs.animating.adjustingOverlayTitle')}
      </h2>
      <Body>
        <Trans
          i18nKey="docs.animating.adjustingOverlayBody"
          components={{ code: <MenuPath>{''}</MenuPath> }}
        />
      </Body>

      <h3 className="font-display text-[15px] font-bold text-content-primary mt-[20px] mb-[6px]">
        {t('docs.animating.opacityTitle')}
      </h3>
      <Body>
        <Trans
          i18nKey="docs.animating.opacityBody"
          components={{ strong: <strong /> }}
        />
      </Body>
      <Note label={t('docs.animating.noteLabel')}>
        {t('docs.animating.opacityNote')}
      </Note>

      <h3 className="font-display text-[15px] font-bold text-content-primary mt-[20px] mb-[6px]">
        {t('docs.animating.showHoverLabelTitle')}
      </h3>
      <Body>{t('docs.animating.showHoverLabelBody')}</Body>

      <h3 className="font-display text-[15px] font-bold text-content-primary mt-[20px] mb-[6px]">
        {t('docs.animating.smoothMeshTitle')}
      </h3>
      <Body>
        <Trans
          i18nKey="docs.animating.smoothMeshBody"
          components={{ code: <MenuPath>{''}</MenuPath> }}
        />
      </Body>

      <h3 className="font-display text-[15px] font-bold text-content-primary mt-[20px] mb-[6px]">
        {t('docs.animating.disableOnNewSceneTitle')}
      </h3>
      <Body>{t('docs.animating.disableOnNewSceneBody')}</Body>

      <SectionDivider />

      {/* -- Referenced scenes -- */}
      <h2
        id="referenced-scenes"
        className="font-display text-[24px] font-extrabold text-content-primary  mb-[16px] tracking-[-0.4px]"
      >
        {t('docs.animating.referencedScenesTitle')}
      </h2>
      <Body>{t('docs.animating.referencedScenesBody')}</Body>

      <Warning label={t('docs.animating.warningLabel')}>
        {t('docs.animating.referencedScenesWarning')}
      </Warning>

      <SectionDivider />

      {/* -- See also: Drawing Poses -- */}
      <h2
        id="see-also-drawing-poses"
        className="font-display text-[24px] font-extrabold text-content-primary  mb-[16px] tracking-[-0.4px]"
      >
        {t('docs.animating.seeAlsoTitle')}
      </h2>
      <Body>
        <Trans
          i18nKey="docs.animating.seeAlsoDrawingPoses"
          components={{
            usec: (
              <Link
                to="/touchpose/docs/drawing-poses"
                className="text-blue-link no-underline font-medium"
              />
            )
          }}
        />
      </Body>
    </article>
  );
};

export default AnimatingDoc;
